import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import anthropicLogo from '../assets/icons/anthropic-logo.svg';
import openaiLogo from '../assets/icons/openai-logo.svg';
import { AI_SOURCE } from '../constants/ai';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entriesBySymbol = {};
  const exitsBySymbol = {};

  const sortedRecords = [...records].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  sortedRecords.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entriesBySymbol[symbol]) entriesBySymbol[symbol] = [];
      entriesBySymbol[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp,
        aiRecommendations: record.aiRecommendations
      });
    } else {
      if (!exitsBySymbol[symbol]) exitsBySymbol[symbol] = [];
      exitsBySymbol[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp
      });
    }
  });

  const pairs = [];
  Object.keys(entriesBySymbol).forEach(symbol => {
    const entries = [...entriesBySymbol[symbol]];
    const exits = exitsBySymbol[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          profit: (exit.price - entry.price) * qty,
          entry: { uiRecord: { aiRecommendations: entry.aiRecommendations } }
        });
      }
    });
  });

  return pairs;
};

// Source configuration
const SOURCE_CONFIG = {
  claude: {
    label: 'Claude',
    logo: anthropicLogo,
    color: '#8b5cf6',
    bgColor: '#f5f3ff',
    borderColor: '#ddd6fe'
  },
  gpt: {
    label: 'GPT',
    logo: openaiLogo,
    color: '#22c55e',
    bgColor: '#f0fdf4',
    borderColor: '#bbf7d0'
  },
  both: {
    label: 'Both AIs',
    logos: [anthropicLogo, openaiLogo],
    color: '#3b82f6',
    bgColor: '#eff6ff',
    borderColor: '#bfdbfe'
  },
  own: {
    label: 'Manual',
    icon: '👤',
    color: '#6b7280',
    bgColor: '#f9fafb',
    borderColor: '#e5e7eb'
  }
};

/**
 * AI Accuracy Chart
 * Compares performance of different recommendation sources:
 * - Claude AI
 * - GPT AI
 * - Both AIs (consensus)
 * - Manual (user's own analysis)
 */
const AIAccuracyChart = ({ records, pairs = [] }) => {
  // Calculate performance by source (with fallback to records)
  const performanceData = useMemo(() => {
    // Use provided pairs or calculate from records
    const pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    // Initialize stats for each source
    const stats = {
      claude: { wins: 0, losses: 0, totalPnl: 0, count: 0 },
      gpt: { wins: 0, losses: 0, totalPnl: 0, count: 0 },
      both: { wins: 0, losses: 0, totalPnl: 0, count: 0 },
      manual: { wins: 0, losses: 0, totalPnl: 0, count: 0 }
    };

    pairsToUse.forEach(pair => {
      // Get the followedRecommendation from the entry record
      const source = pair.entry?.uiRecord?.aiRecommendations?.followedRecommendation
                  || pair.followedRecommendation
                  || AI_SOURCE.MANUAL;

      // Normalize source (in case of unexpected values)
      const VALID_SOURCES = [AI_SOURCE.CLAUDE, AI_SOURCE.GPT, AI_SOURCE.BOTH, AI_SOURCE.MANUAL];
      const normalizedSource = VALID_SOURCES.includes(source) ? source : AI_SOURCE.MANUAL;

      const target = stats[normalizedSource];
      target.count += 1;
      target.totalPnl += pair.profit || 0;
      if ((pair.profit || 0) > 0) {
        target.wins += 1;
      } else {
        target.losses += 1;
      }
    });

    // Calculate derived metrics
    const summary = {};
    Object.keys(stats).forEach(source => {
      const s = stats[source];
      summary[source] = {
        ...s,
        winRate: s.count > 0 ? (s.wins / s.count) * 100 : 0,
        avgPnl: s.count > 0 ? s.totalPnl / s.count : 0
      };
    });

    // Build chart data for bar comparison
    const chartData = [
      {
        metric: 'Win Rate (%)',
        Claude: summary.claude.winRate,
        GPT: summary.gpt.winRate,
        'Both AIs': summary.both.winRate,
        Manual: summary.own.winRate,
        isWinRate: true
      },
      {
        metric: 'Avg P&L ($)',
        Claude: summary.claude.avgPnl,
        GPT: summary.gpt.avgPnl,
        'Both AIs': summary.both.avgPnl,
        Manual: summary.own.avgPnl,
        isWinRate: false
      }
    ];

    // Find the best performer
    const activeSources = Object.entries(summary).filter(([_, data]) => data.count > 0);
    let bestSource = null;
    if (activeSources.length > 1) {
      bestSource = activeSources.reduce((best, [source, data]) => {
        if (!best || data.winRate > summary[best].winRate) {
          return source;
        }
        return best;
      }, null);
    }

    return { stats, summary, chartData, bestSource };
  }, [records, pairs]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isWinRate = payload[0]?.payload?.isWinRate;

      return (
        <div style={{
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</div>
          {payload.map((entry, index) => (
            <div key={index} style={{ fontSize: '13px', marginBottom: '4px' }}>
              <span style={{ color: entry.color }}>● {entry.name}: </span>
              <span style={{ fontWeight: '600' }}>
                {isWinRate
                  ? `${entry.value.toFixed(1)}%`
                  : `$${entry.value.toFixed(2)}`
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const { summary, chartData, bestSource } = performanceData;
  const totalTrades = Object.values(summary).reduce((sum, s) => sum + s.count, 0);

  if (totalTrades === 0) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. AI Accuracy requires matched Entry/Exit pairs.
      </div>
    );
  }

  // Only show sources with trades
  const activeSources = Object.entries(summary).filter(([_, data]) => data.count > 0);
  const isSingleSource = activeSources.length === 1;

  // Helper to render source icon/logo
  const renderSourceIcon = (source, size = 24) => {
    const config = SOURCE_CONFIG[source];
    if (config.logo) {
      return <img src={config.logo} alt={config.label} style={{ width: size, height: size }} />;
    } else if (config.logos) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <img src={config.logos[0]} alt="Claude" style={{ width: size * 0.8, height: size * 0.8 }} />
          <span style={{ fontSize: '12px', color: '#999' }}>+</span>
          <img src={config.logos[1]} alt="GPT" style={{ width: size * 0.8, height: size * 0.8 }} />
        </div>
      );
    } else {
      return <span style={{ fontSize: size }}>{config.icon}</span>;
    }
  };

  return (
    <div style={{ width: '100%', height: isSingleSource ? 400 : 600 }}>
      {/* Single Source: Enlarged Card Layout */}
      {isSingleSource ? (
        <div style={{ padding: '20px' }}>
          {activeSources.map(([source, data]) => {
            const config = SOURCE_CONFIG[source];
            return (
              <div
                key={source}
                style={{
                  padding: '24px',
                  background: config.bgColor,
                  borderRadius: '12px',
                  border: `2px solid ${config.borderColor}`
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px',
                  paddingBottom: '16px',
                  borderBottom: `1px solid ${config.borderColor}`
                }}>
                  {renderSourceIcon(source, 32)}
                  <span style={{ fontWeight: '600', fontSize: '20px', color: config.color }}>
                    {config.label}
                  </span>
                  <span style={{ color: '#666', fontSize: '14px', marginLeft: 'auto' }}>
                    Trading Performance
                  </span>
                </div>

                {/* Stats Grid - Enlarged */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '24px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>Trades</div>
                    <div style={{ fontWeight: '700', fontSize: '28px', color: '#333' }}>{data.count}</div>
                    <div style={{ color: '#999', fontSize: '12px' }}>{data.wins}W / {data.losses}L</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>Win Rate</div>
                    <div style={{
                      fontWeight: '700',
                      fontSize: '28px',
                      color: data.winRate >= 50 ? '#22c55e' : '#ef4444'
                    }}>
                      {data.winRate.toFixed(0)}%
                    </div>
                    <div style={{ color: '#999', fontSize: '12px' }}>
                      {data.winRate >= 70 ? 'Excellent' : data.winRate >= 50 ? 'Good' : 'Needs Work'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>Avg P&L</div>
                    <div style={{
                      fontWeight: '700',
                      fontSize: '28px',
                      color: data.avgPnl >= 0 ? '#22c55e' : '#ef4444'
                    }}>
                      {data.avgPnl >= 0 ? '+' : ''}${data.avgPnl.toFixed(2)}
                    </div>
                    <div style={{ color: '#999', fontSize: '12px' }}>per trade</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>Total P&L</div>
                    <div style={{
                      fontWeight: '700',
                      fontSize: '28px',
                      color: data.totalPnl >= 0 ? '#22c55e' : '#ef4444'
                    }}>
                      {data.totalPnl >= 0 ? '+' : ''}${data.totalPnl.toFixed(2)}
                    </div>
                    <div style={{ color: '#999', fontSize: '12px' }}>cumulative</div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Hint for single source */}
          <div style={{
            marginTop: '20px',
            padding: '12px 16px',
            background: '#f8f9fa',
            borderRadius: '8px',
            color: '#666',
            fontSize: '13px',
            textAlign: 'center'
          }}>
            💡 Trade with different AI recommendations to see comparison charts
          </div>
        </div>
      ) : (
        <>
          {/* Multiple Sources: Table + Bar Chart */}
          {/* Comparison Table */}
          <div style={{
            marginBottom: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#333' }}>Source</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Trades</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Win Rate</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Avg P&L</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#333' }}>Total P&L</th>
                </tr>
              </thead>
              <tbody>
                {activeSources.map(([source, data], index) => {
                  const config = SOURCE_CONFIG[source];
                  const isBest = bestSource === source;
                  return (
                    <tr
                      key={source}
                      style={{
                        background: isBest ? config.bgColor : (index % 2 === 0 ? 'white' : '#fafafa'),
                        borderTop: '1px solid #e0e0e0'
                      }}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {renderSourceIcon(source, 22)}
                          <span style={{ fontWeight: '600', color: config.color }}>{config.label}</span>
                          {isBest && (
                            <span style={{
                              background: '#22c55e',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>BEST</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>
                        {data.count}
                        <span style={{ color: '#999', fontWeight: 'normal', fontSize: '12px', marginLeft: '4px' }}>
                          ({data.wins}W/{data.losses}L)
                        </span>
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '16px',
                        color: data.winRate >= 50 ? '#22c55e' : '#ef4444'
                      }}>
                        {data.winRate.toFixed(0)}%
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: data.avgPnl >= 0 ? '#22c55e' : '#ef4444'
                      }}>
                        {data.avgPnl >= 0 ? '+' : ''}${data.avgPnl.toFixed(2)}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: data.totalPnl >= 0 ? '#22c55e' : '#ef4444'
                      }}>
                        {data.totalPnl >= 0 ? '+' : ''}${data.totalPnl.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Bar Chart Comparison */}
          <ResponsiveContainer height={280}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, bottom: 20, left: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="metric"
                stroke="#666"
                fontSize={13}
              />
              <YAxis
                stroke="#666"
                fontSize={13}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {activeSources.map(([source]) => {
                const config = SOURCE_CONFIG[source];
                const dataKey = source === AI_SOURCE.BOTH ? 'Both AIs' : source === AI_SOURCE.MANUAL ? 'Manual' : config.label;
                return (
                  <Bar
                    key={source}
                    dataKey={dataKey}
                    fill={config.color}
                    radius={[4, 4, 0, 0]}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default AIAccuracyChart;
